import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys, type QueryKeyPart } from "@nasam/kernel";
import type {
  SubscriptionDeviceAllocationInput,
  SubscriptionModuleInput,
  SubscriptionSale,
  SubscriptionsListQuery,
  SubscriptionsRepository,
  SubscriptionWriteInput,
} from "../domain";

function params(
  query: SubscriptionsListQuery | undefined,
): Readonly<Record<string, QueryKeyPart>> | undefined {
  return query === undefined ? undefined : { ...query };
}
export function useSubscriptionsQuery(
  repository: SubscriptionsRepository,
  query?: SubscriptionsListQuery,
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.subscriptions.list(params(query)),
    queryFn: () => repository.list(query),
    enabled,
  });
}
export function useSubscriptionQuery(
  repository: SubscriptionsRepository,
  id: string,
) {
  return useQuery({
    queryKey: queryKeys.subscriptions.detail(id),
    queryFn: () => repository.getById(id),
    enabled: id.length > 0,
  });
}
export function useSubscriptionDeviceTypesQuery(
  repository: SubscriptionsRepository,
  enabled = false,
) {
  return useQuery({
    queryKey: [...queryKeys.subscriptions.all, "device-types"] as const,
    queryFn: () => repository.listDeviceTypes(),
    enabled,
  });
}
export function useSubscriptionDeviceModelsQuery(
  repository: SubscriptionsRepository,
  deviceTypeId: string,
) {
  return useQuery({
    queryKey: [
      ...queryKeys.subscriptions.all,
      "device-models",
      deviceTypeId,
    ] as const,
    queryFn: () => repository.listDeviceModels(deviceTypeId),
    enabled: deviceTypeId.length > 0,
  });
}
function useInvalidatingMutation<TInput, TResult>(
  mutationFn: (input: TInput) => Promise<TResult>,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: async () =>
      client.invalidateQueries({ queryKey: queryKeys.subscriptions.all }),
  });
}
/** A sale also moves the seller's module capacity, so refresh both. */
function useSaleMutation<TInput>(
  mutationFn: (input: TInput) => Promise<SubscriptionSale>,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: async () =>
      Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.subscriptions.all }),
        client.invalidateQueries({ queryKey: queryKeys.modules.all }),
      ]),
  });
}
export function useCreateSubscriptionMutation(
  repository: SubscriptionsRepository,
) {
  return useSaleMutation((input: SubscriptionWriteInput) =>
    repository.create(input),
  );
}
export function useAddSubscriptionModulesMutation(
  repository: SubscriptionsRepository,
) {
  return useSaleMutation(
    ({
      id,
      modules,
    }: {
      id: string;
      modules: readonly SubscriptionModuleInput[];
    }) => repository.addModules(id, modules),
  );
}
export function useAddSubscriptionDevicesMutation(
  repository: SubscriptionsRepository,
) {
  return useInvalidatingMutation(
    ({
      id,
      devices,
    }: {
      id: string;
      devices: readonly SubscriptionDeviceAllocationInput[];
    }) => repository.addDevices(id, devices),
  );
}
